
import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { FaAndroid, FaGlobe } from "react-icons/fa";
import { useIsMobile } from "@/hooks/use-mobile";
const GameShowcase = () => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  // Transform values for parallax effect
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 1.5]);
  const borderRadius = useTransform(scrollYProgress, [0, 0.5], [35, 0]);
  const width = useTransform(scrollYProgress, [0, 0.5], ["100%", "100vw"]);
  const height = useTransform(scrollYProgress, [0, 0.5], ["auto", "100vh"]);
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);

  // Adjust opacity fade out threshold based on device
  const opacityOut = useTransform(scrollYProgress, isMobile ? [0.1, 0.9] : [0.3, 0.5], [1, 0], { clamp: false });

  // For scroll down
  const contentOpacityDown = useTransform(scrollYProgress, isMobile ? [0.1, 0.9] : [0, 0.2], [1, 1], { clamp: false });

  // For scroll up
  const contentOpacityUp = useTransform(scrollYProgress, isMobile ? [0.95, 0.8] : [0.9, 0.7], [1, 0], { clamp: false });

  // Combine based on scroll direction
  const contentOpacity = useTransform(
    scrollYProgress,
    (latest) => latest > scrollYProgress.getPrevious() ? contentOpacityDown.get() : contentOpacityUp.get()
  );

  // Track download clicks for analytics
  const trackDownload = (platform: string) => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'game_download_click', {
        event_category: 'conversion',
        event_label: `${platform} Download - Game Showcase`,
        platform: platform
      });
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setIsVisible(entry.isIntersecting);
    }, {
      threshold: 0.1
    });
    const element = containerRef.current;
    if (element) {
      observer.observe(element);
    }
    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);
  return <>
      <section ref={containerRef} className="relative min-h-screen bg-transparent">
        <div className="absolute inset-0 bg-grid-white/[0.02] -z-10" />
        <div className="absolute inset-0 bg-gradient-to-t via-transparent -z-10" />

        {/* First section with "Take the reins" content */}
        <div className={`${!isMobile ? "sticky top-0 h-screen" : "min-h-screen pt-24"} w-full flex items-center`}>
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div 
                className="relative -ml-24 max-w-[600px]"
                style={{ scale, y }}
                initial={{ opacity: 0, x: -50 }}
                animate={{ 
                  opacity: isVisible ? 1 : 0,
                  x: isVisible ? 0 : -50,
                  transition: { 
                    delay: 0,
                    duration: 0.7,
                    ease: "easeOut"
                  }
                }}
                exit={{ opacity: 0, transition: { duration: 0 } }}
              >
                <motion.div 
                  className="relative z-10 bg-[#222] p-2 shadow-xl ring-1 ring-gray-900/10"
                  style={{ borderRadius }}
                  animate={{
                    transition: {
                      duration: 0.7,
                      ease: "easeOut"
                    }
                  }}
                >
                  <motion.div 
                    className="overflow-hidden bg-black"
                    style={{ borderRadius: useTransform(scrollYProgress, [0, 0.5], [35, 0]) }}
                  >
                    <img 
                      alt="Game view" 
                      className="w-full aspect-[16/9] object-cover" 
                      src="/lovable-uploads/FRRMWebsiteParalax02-Image.webp" 
                    />
                  </motion.div>
                  <motion.div 
                    style={{ opacity: opacityOut }}
                    className="absolute right-0 top-24 h-20 w-1 bg-gray-800 rounded-r-lg"
                  />
                </motion.div>
                <motion.div 
                  style={{ opacity: opacityOut }}
                  className="absolute -inset-x-4 -inset-y-4 z-0 bg-gradient-to-b from-[#f94933]/20 to-transparent blur-2xl opacity-50"
                />
              </motion.div>

              <motion.div 
                style={{ y }}
                className="text-white space-y-6"
                initial={{ opacity: 0, x: 50 }}
                animate={{
                  opacity: isVisible ? 1 : 0,
                  x: isVisible ? 0 : 50,
                  transition: {
                    delay: 0,
                    duration: 0.7,
                    ease: "easeOut"
                  }
                }}
                exit={{ opacity: 0, transition: { duration: 0 } }}
              >
                <h2 className="text-4xl md:text-5xl font-black uppercase">
                  TAKE THE REINS.<br />
                  <span className="text-[#f94933]">RIDE TO RICHES.</span>
                </h2>
                
                <p>Saddle up for an experience that will have you champing at the bit. Ditch the hats, no need for suits, experience the excitement of our exclusive new racehorse game anytime, anywhere.</p>

                <p className="text-lg font-semibold text-[#f94933]">
                  Download Front Runner: Racehorse Manager for free.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <a 
                    href="https://play.google.com/apps/testing/com.RTz.TWCGame" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    onClick={() => trackDownload('Android')}
                    className="group flex items-center p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg hover:shadow-[#f94933]/10"
                  >
                    <div className="aspect-square h-12 w-12 rounded-full bg-[#f94933]/10 flex items-center justify-center group-hover:bg-[#f94933]/20 transition-colors">
                      <FaAndroid className="h-6 w-6 text-[#f94933]" />
                    </div>
                    <div className="ml-3 flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-white group-hover:text-[#f94933] transition-colors truncate">Android</h3>
                    </div>
                    <span className="ml-2 text-[#f94933] opacity-0 group-hover:opacity-100 transition-opacity">
                      →
                    </span>
                  </a>

                  <a 
                    href="https://web.frontrunnergame.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={() => trackDownload('Web')}
                    className="group flex items-center p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg hover:shadow-[#f94933]/10"
                  >
                    <div className="aspect-square h-12 w-12 rounded-full bg-[#f94933]/10 flex items-center justify-center group-hover:bg-[#f94933]/20 transition-colors">
                      <FaGlobe className="h-6 w-6 text-[#f94933]" />
                    </div>
                    <div className="ml-3 flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-white group-hover:text-[#f94933] transition-colors truncate">Web</h3>
                    </div>
                    <span className="ml-2 text-[#f94933] opacity-0 group-hover:opacity-100 transition-opacity">
                      →
                    </span>
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </>;
};
export default GameShowcase;
